import {
  CanActivate,
  ExecutionContext,
  Injectable,
  ForbiddenException,
  Optional,
} from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';
import { AuthenticatedRequest } from './auth.guard';

const PLATFORM_ROLES = ['admin', 'platform_admin', 'account_manager', 'operator'];

@Injectable()
export class AdminAccessGuard implements CanActivate {
  constructor(
    private readonly prisma: PrismaService,
    @Optional() private readonly storeContext?: StoreContextService
  ) {}

  async canActivate(context: ExecutionContext): Promise<boolean> {
    const request = context.switchToHttp().getRequest<AuthenticatedRequest>();
    const user = request.user;

    if (!user) throw new ForbiddenException('Admin access is required');

    // Platform / legacy-admin roles have blanket access
    if (PLATFORM_ROLES.includes(user.role)) return true;

    // Merchant roles must be active staff of the store currently in
    // context — an active staff assignment at a *different* store must
    // never grant access here, or any merchant could reach every tenant's
    // admin data just by switching the store-id header.
    const staff = await this.prisma.storeStaff.findFirst({
      where: { storeId: this.storeId(), userId: user.id, isActive: true },
      select: { id: true },
    });

    if (!staff) {
      throw new ForbiddenException(await this.explain(request, user.id));
    }

    return true;
  }

  /**
   * Why access was refused, when it can be said safely.
   *
   * "Admin access is required" is true but useless when the real cause is a
   * *deactivated store*: the tenancy middleware only trusts an `x-store-id` for
   * an active store, so a stale selection silently drops the request to the
   * default tenant and every endpoint refuses. The message named permissions
   * and cost a long hunt in the wrong place.
   *
   * The clearer message is only given for a store this user is genuinely staff
   * of. Saying "that store is deactivated" about any id would let someone probe
   * which store ids exist, so the check is scoped to their own assignments and
   * reveals nothing they could not already see on their My Stores page.
   */
  private async explain(request: AuthenticatedRequest, userId: string): Promise<string> {
    const generic = 'Admin access is required';

    const header = request.headers?.['x-store-id'];
    const requested = Array.isArray(header) ? header[0] : header;
    if (!requested) return generic;

    try {
      const own = await this.prisma.storeStaff.findFirst({
        where: { storeId: requested, userId, isActive: true },
        select: { store: { select: { isActive: true, name: true } } },
      });

      if (own && !own.store?.isActive) {
        return `“${own.store?.name ?? 'That store'}” is deactivated. Reactivate it, or switch to another store, to continue.`;
      }
    } catch {
      // This runs only to improve an error that is already being thrown.
      // Failing here would turn a clean 403 into a 500 and hide the refusal
      // behind a crash, which is strictly worse than the generic wording.
    }

    return generic;
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
